/**
 * <blyss-carousel> — arrows + page dots for a CSS scroll-snap track.
 *
 * Scrolling is native (overflow-x + scroll-snap), so the track remains
 * swipeable and keyboard-scrollable with JS disabled; this adds controls and
 * state on top. Dots represent PAGES derived from scroll width, not cards, so
 * they stay correct as cards-per-view changes across breakpoints without the
 * JS needing to know the breakpoints.
 */
class BlyssCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-bc-track]');
    this.dotsEl = this.querySelector('[data-bc-dots]');
    this.prevBtn = this.querySelector('[data-bc-prev]');
    this.nextBtn = this.querySelector('[data-bc-next]');
    if (!this.track) return;

    this.onScroll = this.#throttle(() => this.#update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.#page(-1));
    this.nextBtn?.addEventListener('click', () => this.#page(1));

    // Cards-per-view changes on resize, so page count must be recomputed.
    this.resizeObserver = new ResizeObserver(this.#throttle(() => this.#build()));
    this.resizeObserver.observe(this.track);

    this.#build();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  #throttle(fn) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn();
      });
    };
  }

  get #pageCount() {
    const { scrollWidth, clientWidth } = this.track;
    if (!clientWidth) return 1;
    // Tolerance absorbs sub-pixel rounding that would otherwise invent a page.
    return Math.max(1, Math.round((scrollWidth - 2) / clientWidth));
  }

  #build() {
    const pages = this.#pageCount;
    const single = pages <= 1;

    this.toggleAttribute('data-bc-single', single);
    if (this.prevBtn) this.prevBtn.hidden = single;
    if (this.nextBtn) this.nextBtn.hidden = single;

    if (this.dotsEl) {
      this.dotsEl.hidden = single;
      if (this.dots?.length !== pages) {
        this.dotsEl.replaceChildren();
        this.dots = Array.from({ length: pages }, (_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'blyss-bs__dot';
          dot.setAttribute('aria-label', `Go to page ${i + 1} of ${pages}`);
          dot.addEventListener('click', () => this.#goTo(i));
          this.dotsEl.appendChild(dot);
          return dot;
        });
      }
    }

    this.#update();
  }

  #currentPage() {
    const { scrollLeft, clientWidth } = this.track;
    if (!clientWidth) return 0;
    return Math.round(Math.abs(scrollLeft) / clientWidth);
  }

  #goTo(index) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // scrollLeft is negative in RTL; derive direction from the existing value.
    const dir = this.track.scrollLeft < 0 ? -1 : 1;
    this.track.scrollTo({
      left: dir * index * this.track.clientWidth,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  #page(delta) {
    const next = Math.min(Math.max(this.#currentPage() + delta, 0), this.#pageCount - 1);
    this.#goTo(next);
  }

  #update() {
    const current = this.#currentPage();
    const pages = this.#pageCount;

    this.dots?.forEach((dot, i) => {
      const active = i === current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.prevBtn) this.prevBtn.disabled = current <= 0;
    if (this.nextBtn) this.nextBtn.disabled = current >= pages - 1;
  }
}

if (!customElements.get('blyss-carousel')) {
  customElements.define('blyss-carousel', BlyssCarousel);
}
